import { ErrorBox, Spinner, Title4 } from '@common/components';
import { Toggle } from '@common/components/ToggleV2';
import Alert from '@common/components/Alert/Alert';
import { PopperRow } from '@common/components/Popper';
import { AvailabilityForm } from '@common/features/DynamicPackagesConfigurationWizardNew/components/AvailabilityForm/AvailabilityForm';
import { useGetSpaPackageConfigGroups } from '@common/features/spa';
import { useTranslations } from '@lang';
import { ErrorWrapper } from '@routes/CheckoutBilling/components/BillingForm.styles';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { PackagesFormValues, SelectedExperienceType } from '../../../../types';
import {
  SpaAvailabilityWrapper,
  TitleWrapper,
  ToggleWrapper,
  SpaLabel,
} from '../MultiDayExperiencesStep.styles';
import { usePackageConfiguration } from '../../../../contexts';
import { getEffectiveExperienceDate } from '@common/features/DynamicPackagesConfigurationWizardNew/helpers/getEffectiveExperienceDate';
import { findOptionFromConfigGroups } from '@common/features/DynamicPackagesConfigurationWizardNew/helpers/findOptionFromConfigGroups';

type Props = {
  experience: SelectedExperienceType;
  configurationGroups: ConfigurationGroupType[];
  experienceIndex: number;
  dayNumberRange: number[];
  guestIndex?: number;
  numberOfGuests?: number;
  isSameTimeForAllGuests?: boolean;
  onToggleSameTime?: () => void;
};

export const SingleOptionExperienceTimeslotSelection: React.FC<Props> = ({
  experience,
  configurationGroups,
  experienceIndex,
  dayNumberRange,
  guestIndex = 0,
  numberOfGuests = 1,
  isSameTimeForAllGuests = false,
  onToggleSameTime,
}) => {
  const { property, spaPackageId, spaOpenTime, spaCloseTime, journeyParams } =
    usePackageConfiguration();

  const { watch } = useFormContext<PackagesFormValues>();
  const minimumTimeslotQuantity = isSameTimeForAllGuests ? numberOfGuests : 1;
  const { t } = useTranslations();
  const delayAfter = watch(`selectedExperiences.${experienceIndex}.delayAfter`);
  const guests = watch('guests') || [];
  const guest = guests[guestIndex];

  const selectedDayNumber = watch(`guests.${guestIndex}.experiences.${experienceIndex}.dayNumber`);

  // Use the selected day, or default to the first day in the range
  const { formattedDate: relevantDate } = getEffectiveExperienceDate(
    journeyParams.fromDate,
    selectedDayNumber,
    dayNumberRange
  );

  const {
    data: configGroups,
    isFetching,
    error,
  } = useGetSpaPackageConfigGroups(
    configurationGroups,
    property.id,
    Number(spaPackageId),
    relevantDate,
    isSameTimeForAllGuests ? numberOfGuests : 1,
    spaOpenTime,
    spaCloseTime
  );

  const option = findOptionFromConfigGroups(configGroups, Number(experience.optionId));

  if (error) {
    return (
      <ErrorWrapper>
        <ErrorBox error={{ id: 'error.reservation_failed' }} />
      </ErrorWrapper>
    );
  }

  if (!option) {
    return (
      <PopperRow>
        <Alert content={t('common.unavailable')} />
      </PopperRow>
    );
  }

  return (
    <>
      <Spinner isActive={isFetching} isBackgroundDisabled={true} />

      {onToggleSameTime && (
        <TitleWrapper>
          <Title4>{option.name}</Title4>
          {numberOfGuests > 1 && onToggleSameTime && (
            <ToggleWrapper>
              <Toggle
                input={{
                  value: isSameTimeForAllGuests,
                  onChange: onToggleSameTime,
                }}
                label={t('overnightPackage.spa.sameOptionForGuests')}
              />
            </ToggleWrapper>
          )}
        </TitleWrapper>
      )}
      <SpaAvailabilityWrapper
        data-testid={`single-option-selection-guest-${guestIndex}-exp-${experience.id}`}
      >
        {!isSameTimeForAllGuests && guest && (
          <SpaLabel>
            {guest.guestDetails?.title} {guest.guestDetails?.firstName}{' '}
            {guest.guestDetails?.lastName}
          </SpaLabel>
        )}
        <AvailabilityForm
          id={experience.id}
          entityType={experience.type}
          timeslots={option.timeslots || []}
          namePrefix={`guests.${guestIndex}.experiences.${experienceIndex}`}
          guestIndex={guestIndex}
          delayAfter={delayAfter}
          dayNumber={undefined}
          dayNumberRange={dayNumberRange}
          experienceIndex={experienceIndex}
          minimumTimeslotQuantity={minimumTimeslotQuantity}
          showDaySelector
        />
      </SpaAvailabilityWrapper>
    </>
  );
};
